<template>
  <div class="qr-code-wrapper">
    <div ref="qrCodeRef"></div>
  </div>
</template>

<script setup lang="ts">
import QRCodeStyling, { Options } from 'qr-code-styling';
import { onMounted, ref, watch } from 'vue';

const emit = defineEmits<{
  (e: 'qr-ready'): void;
}>();

const props = defineProps<{
  value: string;
  logoUrl?: string;
  width?: number;
  height?: number;
  showLogo?: boolean;
}>();

const qrCodeRef = ref<HTMLElement | null>(null);
const validLogoUrl = ref<string | undefined>();
const imageSizePercent = ref(0.4);

const width = props.width || 16;
const height = props.height || 160;

const qrInstance = new QRCodeStyling({
  width,
  height,
  type: 'canvas',
  data: props.value,
  margin: 0,
  qrOptions: {
    typeNumber: 0,
    mode: 'Byte',
    errorCorrectionLevel: 'Q',
  },
  imageOptions: {
    hideBackgroundDots: true,
    imageSize: imageSizePercent.value,
    margin: 0,
    crossOrigin: 'anonymous',
    saveAsBlob: true,
  },
  dotsOptions: {
    type: 'dots',
    color: '#6a1a4c',
    roundSize: true,
    gradient: {
      type: 'linear',
      rotation: 0,
      colorStops: [
        { offset: 0, color: '#121112' },
        { offset: 1, color: '#0c0c0d' },
      ],
    },
  },
  backgroundOptions: {
    color: '#ffffff',
    round: 0,
  },
  cornersSquareOptions: {
    type: 'extra-rounded',
    color: '#000000',
  },
  cornersDotOptions: {
    type: undefined,
    color: '#000000',
  },
});

function clearAndRender() {
  if (!qrCodeRef.value) return;
  qrCodeRef.value.innerHTML = '';
  qrInstance.append(qrCodeRef.value);
  emit('qr-ready');
}

function updateQRCode() {
  const hasLogo = props.showLogo && validLogoUrl.value;

  const updateOptions: Partial<Options> = {
    data: props.value,
  };

  if (hasLogo) {
    updateOptions.image = validLogoUrl.value;
    updateOptions.imageOptions = {
      hideBackgroundDots: true,
      margin: 0,
      imageSize: imageSizePercent.value,
      crossOrigin: 'anonymous',
      saveAsBlob: true,
    };
  }

  qrInstance.update(updateOptions);
  emit('qr-ready');
}

function validateImage(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxPx = Math.min(width, height) * 0.4;
      const longest = Math.max(img.width, img.height);
      imageSizePercent.value = longest > maxPx ? maxPx / longest : 0.4;
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

watch(
  () => props.logoUrl,
  async (url) => {
    validLogoUrl.value = undefined;
    if (props.showLogo && url && (await validateImage(url))) {
      validLogoUrl.value = url;
    }
    updateQRCode();
  },
  { immediate: true },
);

watch(() => props.value, updateQRCode);

onMounted(() => {
  clearAndRender();
});
</script>

<style scoped>
.qr-code-wrapper {
  width: 350px;
  padding: 0; /* reduced padding */
  border-radius: 10px;
  border: none; /* optionally remove if visual border is undesired */
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  height: 350px;
}

.qr-code-wrapper :deep(canvas) {
  /* max-width: 100%;
  max-height: 100%; */
  width: 350px !important;
  height: 350px !important;
  object-fit: contain;
  display: block;
}
</style>
